import { useState, useEffect } from 'react'
import { collection, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { ArtistOnboarding } from '@/types/models'

export function useOnboardings() {
  const [onboardings, setOnboardings] = useState<ArtistOnboarding[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'onboardings'),
      (snapshot) => {
        try {
          const onboardingList: ArtistOnboarding[] = []
          
          snapshot.forEach((doc) => {
            const data = doc.data()
            onboardingList.push({
              id: doc.id,
              bio: data.bio || '',
              displayName: data.displayName || '',
              firstName: data.firstName || undefined,
              lastName: data.lastName || undefined,
              email: data.email || undefined,
              userId: data.userId || undefined,
              postUrls: data.postUrls || [],
              profileImageUrl: data.profileImageUrl || '',
              backdropUrl: data.backdropUrl || undefined,
              status: data.status || 'scraper_started',
              location: data.location || '',
              lat: data.lat || undefined,
              lon: data.lon || undefined,
              tattooImageUrls: data.tattooImageUrls || [],
              code: data.code || undefined,
              currentStep: data.currentStep || undefined,
              flashes: data.flashes || [],
              agreementAccepted: data.agreementAccepted || false,
              latestError: data.latestError || undefined,
              invitingArtist: typeof data.invitingArtist === 'string' ? data.invitingArtist : undefined,
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined,
              updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined,
            })
          })

          // Sort by creation date, newest first
          onboardingList.sort((a, b) => {
            if (!a.createdAt && !b.createdAt) return 0
            if (!a.createdAt) return 1
            if (!b.createdAt) return -1
            return b.createdAt.getTime() - a.createdAt.getTime()
          })

          setOnboardings(onboardingList)
          setLoading(false)
        } catch (err) {
          console.error('Error processing onboardings:', err)
          setError(err instanceof Error ? err : new Error('Failed to fetch onboardings'))
          setLoading(false)
        }
      },
      (err) => {
        console.error('Error fetching onboardings:', err)
        
        // Provide more specific error messages
        let errorMessage = 'Failed to fetch onboardings'
        if (err.code === 'permission-denied') {
          errorMessage = 'Missing or insufficient permissions. Please ensure you are signed in with an admin account and try refreshing the page.'
        } else if (err.code === 'unauthenticated') {
          errorMessage = 'Authentication required. Please sign in with your admin account.'
        }
        
        setError(err instanceof Error ? new Error(errorMessage) : new Error(errorMessage))
        setLoading(false)
      }
    )

    return unsubscribe
  }, [])

  const updateOnboarding = async (id: string, updates: Partial<ArtistOnboarding>): Promise<void> => {
    try {
      const docRef = doc(db, 'onboardings', id)
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      }
      
      // Remove the id field from updates to avoid Firestore error
      delete (updateData as any).id
      
      await updateDoc(docRef, updateData)
    } catch (err) {
      console.error('Error updating onboarding:', err)
      throw err instanceof Error ? err : new Error('Failed to update onboarding')
    }
  }

  const deleteOnboarding = (id: string): void => {
    setOnboardings(prev => prev.filter(onboarding => onboarding.id !== id))
  }

  return {
    onboardings,
    loading,
    error,
    updateOnboarding,
    deleteOnboarding
  }
}

export function useOnboardingDetail(onboardingId: string | undefined) {
  const [onboarding, setOnboarding] = useState<ArtistOnboarding | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!onboardingId) {
      setOnboarding(null)
      setLoading(false)
      return
    }

    const unsubscribe = onSnapshot(
      doc(db, 'onboardings', onboardingId),
      (doc) => {
        try {
          if (!doc.exists()) {
            setOnboarding(null)
            setLoading(false)
            return
          }

          const data = doc.data()
          const onboardingData: ArtistOnboarding = {
            id: doc.id,
            bio: data.bio || '',
            displayName: data.displayName || '',
            firstName: data.firstName || undefined,
            lastName: data.lastName || undefined,
            email: data.email || undefined,
            userId: data.userId || undefined,
            postUrls: data.postUrls || [],
            profileImageUrl: data.profileImageUrl || '',
            backdropUrl: data.backdropUrl || undefined,
            status: data.status || 'scraper_started',
            location: data.location || '',
            lat: data.lat || undefined,
            lon: data.lon || undefined,
            tattooImageUrls: data.tattooImageUrls || [],
            code: data.code || undefined,
            currentStep: data.currentStep || undefined,
            flashes: data.flashes || [],
            agreementAccepted: data.agreementAccepted || false,
            latestError: data.latestError || undefined,
            invitingArtist: typeof data.invitingArtist === 'string' ? data.invitingArtist : undefined,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : undefined,
            updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined,
          }

          setOnboarding(onboardingData)
          setLoading(false)
        } catch (err) {
          console.error('Error processing onboarding detail:', err)
          setError(err instanceof Error ? err : new Error('Failed to fetch onboarding detail'))
          setLoading(false)
        }
      },
      (err) => {
        console.error('Error fetching onboarding detail:', err)
        
        // Provide more specific error messages
        let errorMessage = 'Failed to fetch onboarding detail'
        if (err.code === 'permission-denied') {
          errorMessage = 'Missing or insufficient permissions. Please ensure you are signed in with an admin account and try refreshing the page.'
        } else if (err.code === 'unauthenticated') {
          errorMessage = 'Authentication required. Please sign in with your admin account.'
        }
        
        setError(err instanceof Error ? new Error(errorMessage) : new Error(errorMessage))
        setLoading(false)
      }
    )

    return unsubscribe
  }, [onboardingId])

  // Helper function to recursively remove undefined values (Firestore doesn't allow them)
  const removeUndefinedValues = (obj: any): any => {
    if (obj === null) {
      return null
    }
    
    if (obj === undefined) {
      return undefined
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => removeUndefinedValues(item)).filter(item => item !== undefined)
    }
    
    if (typeof obj === 'object') {
      const cleaned: any = {}
      for (const [key, value] of Object.entries(obj)) {
        const cleanedValue = removeUndefinedValues(value)
        if (cleanedValue !== undefined) {
          cleaned[key] = cleanedValue
        }
      }
      return cleaned
    }
    
    return obj
  }

  const updateOnboarding = async (updates: Partial<ArtistOnboarding>): Promise<void> => {
    if (!onboardingId) return
    
    console.log('[useOnboardings] Updating onboarding (raw):', {
      onboardingId,
      updateKeys: Object.keys(updates),
      flashCount: updates.flashes?.length || 'N/A',
      rawFlashes: updates.flashes?.map((f: any) => ({
        id: f.id,
        imageUrl: f.imageUrl ? 'HAS_URL' : 'UNDEFINED',
        minPrice: f.minPrice,
        minSize: f.minSize,
        cropValidation: f.cropValidation
      })) || []
    })
    
    try {
      const docRef = doc(db, 'onboardings', onboardingId)
      
      // Clean undefined values before sending to Firestore
      const cleanedUpdates = removeUndefinedValues(updates)
      
      const updateData = {
        ...cleanedUpdates,
        updatedAt: Timestamp.now()
      }
      
      // Remove the id field from updates to avoid Firestore error
      delete (updateData as any).id
      
      console.log('[useOnboardings] Sending to Firestore (cleaned):', {
        docPath: `onboardings/${onboardingId}`,
        dataKeys: Object.keys(updateData),
        flashCount: updateData.flashes?.length || 'N/A',
        cleanedFlashes: updateData.flashes?.map((f: any) => ({
          id: f.id,
          imageUrl: f.imageUrl ? 'HAS_URL' : 'NO_URL',
          minPrice: f.minPrice,
          minSize: f.minSize,
          cropValidation: f.cropValidation
        })) || []
      })
      
      await updateDoc(docRef, updateData)
      console.log('[useOnboardings] Firestore update successful')
    } catch (err) {
      console.error('[useOnboardings] Error updating onboarding:', err)
      throw err instanceof Error ? err : new Error('Failed to update onboarding')
    }
  }

  return {
    onboarding,
    loading,
    error,
    updateOnboarding
  }
}
